import { Injectable, EventEmitter, Renderer2, RendererFactory2  } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { Group } from '../models/endpoint-group.model';
import { Router, NavigationStart } from '@angular/router';
import { AppSettings } from '../constants/appSettings.constants';
import { SidebarAction, ContentType } from '../models/site.model';
import { StrapiService } from './strapi.service'; 

@Injectable({
  providedIn: 'root',
})
export class SiteService  {

  private settings = AppSettings;
  private renderer: Renderer2;
  private docsNavLoaded = false;
  
  // Define observables.
  private navSourceSubject$ = new BehaviorSubject<Group[]>([]); 
  currentNavEndpoints$ = this.navSourceSubject$.asObservable();

  private docsNavSourceSubject$ = new BehaviorSubject<Group[]>([]); 
  currentNavDocs$ = this.docsNavSourceSubject$.asObservable();
  
  private activeItemSourceSubject$ = new BehaviorSubject<string>('');
  activeItem$ = this.activeItemSourceSubject$.asObservable();

  private activeGroupSourceSubject$ = new BehaviorSubject<string>('');
  activeGroup$ = this.activeGroupSourceSubject$.asObservable();
    
  private versionSourceSubject$ = new BehaviorSubject<number>( this.settings.general.defaultVersion ); 
  currentVersion$ = this.versionSourceSubject$.asObservable();

  private navOpenSourceSubject$ = new BehaviorSubject<boolean>(window.innerWidth > 768);
  navOpen$ = this.navOpenSourceSubject$.asObservable();

  private themeSourceSubject$ = new BehaviorSubject<string>(this.loadTheme());
  theme$ = this.themeSourceSubject$.asObservable();

  private sidebarActionSubject$ = new Subject<SidebarAction>();
  sidebarActions$ = this.sidebarActionSubject$.asObservable();

  private contentTypeSubject$ = new BehaviorSubject<ContentType>('api');
  contentType$ = this.contentTypeSubject$.asObservable();

  public closeNavGroupsEvent: EventEmitter<void> = new EventEmitter();

  public versionChangedEvent: EventEmitter<void> = new EventEmitter();


  constructor(
    private router: Router,
    private rendererFactory: RendererFactory2,
    private strapiService: StrapiService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.updateContentType(this.router.url);
    this.listenToRouting();
  }
 
  toggleTheme() {
    const theme = (this.themeSourceSubject$.value === 'dark-mode') ? 'light-mode' : 'dark-mode';
    
    localStorage.setItem('theme', theme);
    this.themeSourceSubject$.next(theme);
  }

  loadTheme() {
    const storedTheme = localStorage.getItem('theme') || 'dark-mode';
    return storedTheme;
  }

  toggleSidebar() {
    let navOpenState = !this.navOpenSourceSubject$.value;
    this.navOpenSourceSubject$.next(navOpenState);
    this.sidebarActionSubject$.next(SidebarAction.Toggle);
  }

  closeSidebar() {
    this.navOpenSourceSubject$.next(false);
    this.sidebarActionSubject$.next(SidebarAction.Close);
  }

  openSidebar() {
    this.navOpenSourceSubject$.next(true);
    this.sidebarActionSubject$.next(SidebarAction.Open);
  }

  changeVersion(version: number) {
    this.versionSourceSubject$.next(version);
    this.versionChangedEvent.emit();
  }
  
  changeNav(nav: Group[]) {
    this.navSourceSubject$.next(nav);
  }
  
  changeActiveNavItem(item: string, groupCode: string, contentType: ContentType) {
    this.activeItemSourceSubject$.next(item);
    this.activeGroupSourceSubject$.next(groupCode);
    // console.log('Active nav changed:', { item, groupCode, contentType });
  }
  
  refreshDocsNav() {
    this.loadDocsNav();
  }

  private listenToRouting() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.updateContentType(event.url);
        
        if (!event.url.startsWith('/e/')) {
          this.resetActiveNav();
        }
      }
    });
  }

  private updateContentType(url: string) {
    const contentType: ContentType = ( url.startsWith('/docs') || url.startsWith('/preview') ) ? 'docs' : 'api';
    this.contentTypeSubject$.next(contentType);

    if (contentType === 'docs' && !this.docsNavLoaded) {
      this.loadDocsNav();
    }
  }

  private loadDocsNav() {
    this.strapiService.getSideNavDocs().subscribe({
      next: (docs) => {
        this.docsNavSourceSubject$.next(docs);
        this.docsNavLoaded = true;
      },
      error: (err) => {
        console.error('Failed to load docs navigation:', err);
      }
    });
  }

  private resetActiveNav() {
    this.activeItemSourceSubject$.next('none');
    this.activeGroupSourceSubject$.next('none');
    this.closeNavGroupsEvent.emit();
  }

  updateTheme( theme:string ) {
    const classToRemove = (theme === 'dark-mode') ? 'light-mode' : 'dark-mode';

    if (document.body.classList.contains(classToRemove)) {
      this.renderer.removeClass(document.body, classToRemove);
    }

    if (!document.body.classList.contains(theme)) {
      this.renderer.addClass(document.body, theme);
    }
  }

  

}